import React, { createRef, useCallback, useEffect, useState } from 'react';
import { ExampleFile } from '../models/exampleFile.types';
import { useStyles } from './FileInput.styles';

interface FileInputProps {
  onFilesLoaded: (files: FileList) => void;
  acceptedFileExtensions?: string[];
  hint?: string;
  exampleFile?: ExampleFile;
}

const FileInput: React.FC<FileInputProps> = ({ onFilesLoaded, acceptedFileExtensions, hint, exampleFile }) => {
  const [dragCounter, setDragCounter] = useState<number>(0);
  const [drag, setDrag] = useState<boolean>(false);
  const dropRef = createRef<HTMLDivElement>();
  const inputFileRef = createRef<HTMLInputElement>();

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter(dragCounter + 1);
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        setDrag(true);
      }
    },
    [dragCounter],
  );

  const handleDragOut = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter(dragCounter - 1);
      if (dragCounter === 0) {
        setDrag(false);
      }
    },
    [dragCounter],
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDrag(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        // this.props.handleDrop(e.dataTransfer.files)
        onFilesLoaded(e.dataTransfer.files);
        e.dataTransfer.clearData();
        setDragCounter(0);
      }
    },
    [onFilesLoaded],
  );

  useEffect(() => {
    const div = dropRef.current;
    if (div) {
      div.addEventListener('dragenter', handleDragIn);
      div.addEventListener('dragleave', handleDragOut);
      div.addEventListener('dragover', handleDrag);
      div.addEventListener('drop', handleDrop);
    }
    return () => {
      const div = dropRef.current;
      if (div) {
        div.removeEventListener('dragenter', handleDragIn);
        div.removeEventListener('dragleave', handleDragOut);
        div.removeEventListener('dragover', handleDrag);
        div.removeEventListener('drop', handleDrop);
      }
    };
  }, []);

  const [height, setHeight] = useState<number>(0.0);
  useEffect(() => {
    if (dropRef.current !== null) setHeight(dropRef.current.clientHeight);
  });

  const onZoneClick = useCallback(() => {
    if (inputFileRef && inputFileRef.current) {
      inputFileRef.current.click();
    }
  }, [inputFileRef]);

  const handleFileUpload = useCallback(
    (event) => {
      event.stopPropagation();
      event.preventDefault();
      onFilesLoaded(event.target.files);
      if (hint !== undefined) {
        event.target.value = null;
      }
    },
    [onFilesLoaded],
  );

  const styles = useStyles();

  const displayExampleFile = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.MouseEvent<HTMLSpanElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    event.preventDefault();
    if (exampleFile) {
      const blob = new Blob([exampleFile.content], { type: exampleFile.type });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = exampleFile.name;
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className={styles.container} ref={dropRef} onClick={onZoneClick} style={{ minHeight: height + 'px' }}>
      <input
        className={styles.input}
        ref={inputFileRef}
        type="file"
        onChange={handleFileUpload}
        accept={acceptedFileExtensions ? '.' + acceptedFileExtensions.join(',.') : 'image/*'}
        data-testid="inputFile"
      />
      {!drag && (
        <>
          <p className={styles.dragText}>
            Przenieś plik lub&nbsp;<span className={styles.fileText}>wybierz plik z listy</span>
          </p>
          {hint && (
            <p className={[styles.dragText, styles.hint].join(' ')}>
              {hint}&nbsp;
              {exampleFile && (
                <span
                  className={styles.fileText}
                  style={{ fontSize: '12px', cursor: 'pointer' }}
                  onClick={displayExampleFile}
                >
                  zobacz przykładowy plik
                </span>
              )}
            </p>
          )}
        </>
      )}
      {drag && (
        <div className={styles.dragZone}>
          <div className={styles.dragContent}>
            <p className={styles.dropText}>Upuść plik tutaj</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileInput;
